import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Download, X, Loader2, CheckCircle2, XCircle, Layers, CalendarDays } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { invoicesService, Invoice } from '@/services/invoices.service';
import { sucursalesService } from '@/services/sucursales.service';
import { dialogs } from '@/stores/dialog-store';

const todayMX = () => {
  const d = new Date(Date.now() - 6 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

const GLOBAL_ROLES = ['SUPER_ADMIN', 'ADMIN', 'GERENTE_GENERAL', 'CONTADOR'];

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Factura global diaria
  const canGlobal = !!user?.role && GLOBAL_ROLES.includes(user.role);
  const [showGlobalPanel, setShowGlobalPanel] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [globalBranchId, setGlobalBranchId] = useState<string>(() => user?.branch?.id || '');
  const [globalDate, setGlobalDate] = useState(todayMX);
  const [generatingGlobal, setGeneratingGlobal] = useState(false);

  const loadData = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const data = await invoicesService.findAll(user.company.id, search);
      setInvoices(data);
    } catch (error) {
      console.error(error);
      dialogs.alert('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  }, [user, search]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadData();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [loadData, search]);

  useEffect(() => {
    if (!user?.company?.id || !canGlobal) return;
    sucursalesService.findByCompany(user.company.id)
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setBranches(list);
        if (!globalBranchId) {
          setGlobalBranchId(user.branch?.id || list[0]?.id || '');
        }
      })
      .catch(() => setBranches([]));
  }, [user, canGlobal, globalBranchId]);

  const handleCreateGlobal = async () => {
    if (!user?.company?.id || !globalBranchId) {
      dialogs.alert('Selecciona una sucursal para la factura global.');
      return;
    }
    const ok = await dialogs.confirm(
      `Se generará un CFDI global diario (${globalDate}) con las ventas a Público en General de la sucursal seleccionada. ¿Continuar?`,
    );
    if (!ok) return;

    setGeneratingGlobal(true);
    try {
      const inv = await invoicesService.createGlobal(user.company.id, globalBranchId, globalDate);
      dialogs.alert(
        `Factura global ${inv.folio} timbrada con éxito (UUID ${inv.uuid}).\n` +
        `${inv.salesCount ?? 0} operaciones consolidadas por $${Number(inv.grandTotal).toFixed(2)} MXN.`,
      );
      setShowGlobalPanel(false);
      loadData();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'No se pudo generar la factura global';
      dialogs.alert(msg);
    } finally {
      setGeneratingGlobal(false);
    }
  };

  const handleDownload = async (invoice: Invoice, type: 'xml' | 'pdf') => {
    try {
      const ext = type === 'pdf' ? 'pdf' : 'xml';
      const filename = `factura_${invoice.folio}_${invoice.uuid || invoice.id}.${ext}`;
      await invoicesService.download(invoice.id, type, filename);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || `No se pudo descargar el archivo ${type.toUpperCase()}`;
      dialogs.alert(msg);
    }
  };

  const handleCancel = async (invoice: Invoice) => {
    if (invoice.status === 'CANCELLED') return;

    const esGlobal = invoice.kind === 'GLOBAL';
    const confirm = await dialogs.confirm(
      esGlobal
        ? `¿Cancelar la factura global ${invoice.folio} (${invoice.uuid}) ante el SAT?\n\nLas ${invoice.salesCount ?? 0} ventas consolidadas serán liberadas y podrás regenerar la factura global del día.`
        : `¿Estás seguro de cancelar la factura ${invoice.folio} (${invoice.uuid})?\nEsta acción es irreversible ante el SAT.`,
    );
    if (!confirm) return;

    try {
      const res: any = await invoicesService.cancel(invoice.id, 'OPERACION_NO_CONCRETADA');
      const released = Number(res?.releasedSales);
      dialogs.alert(
        esGlobal
          ? `Factura global cancelada exitosamente ante el SAT.\n${released > 0 ? `Se liberaron ${released} ventas. ` : ''}Ya puedes regenerar la factura global del día.`
          : 'Factura cancelada exitosamente ante el SAT.',
      );
      loadData();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'No se pudo cancelar la factura';
      dialogs.alert(msg);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText size={24} className="text-ios-blue" />
            Facturación CFDI
          </h1>
          <p className="text-sm text-ios-gray mt-1">Historial de comprobantes fiscales emitidos</p>
        </div>
        
        <div className="flex items-center gap-3">
          {canGlobal && (
            <button
              onClick={() => setShowGlobalPanel((v) => !v)}
              className="ios-button-ghost flex items-center justify-center gap-2 px-6"
            >
              <Layers size={18} />
              Factura global del día
            </button>
          )}
          <button
            onClick={() => navigate('/invoices/new')}
            className="ios-button flex items-center justify-center gap-2 px-6"
          >
            <Plus size={18} />
            Nueva Factura
          </button>
        </div>
      </div>

      {canGlobal && showGlobalPanel && (
        <div className="ios-card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Layers size={20} className="text-ios-blue" />
              <h2 className="font-bold text-ios-dark">Factura global diaria (Público en General)</h2>
            </div>
            <button onClick={() => setShowGlobalPanel(false)} className="p-1.5 text-ios-gray hover:bg-ios-gray-6 rounded-lg">
              <X size={16} />
            </button>
          </div>
          <p className="text-xs text-ios-gray mb-4">
            Consolida en un solo CFDI las ventas del día realizadas a clientes sin RFC (Público en General).
            Las ventas incluidas quedan marcadas y no podrán facturarse por separado.
          </p>
          <div className="flex flex-col md:flex-row items-end gap-3">
            <div className="flex-1 max-w-xs">
              <label className="block text-[11px] font-bold text-ios-gray uppercase tracking-wider mb-1">Sucursal</label>
              <select
                value={globalBranchId}
                onChange={(e) => setGlobalBranchId(e.target.value)}
                className="ios-input w-full"
              >
                {(branches.length ? branches : []).map((b: any) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
                {branches.length === 0 && (
                  <option value={user?.branch?.id || ''}>{user?.branch?.name || 'Sucursal actual'}</option>
                )}
              </select>
            </div>
            <div className="flex-1 max-w-xs">
              <label className="block text-[11px] font-bold text-ios-gray uppercase tracking-wider mb-1">Fecha del día</label>
              <input
                type="date"
                value={globalDate}
                max={todayMX()}
                onChange={(e) => setGlobalDate(e.target.value)}
                className="ios-input w-full"
              />
            </div>
            <button
              onClick={handleCreateGlobal}
              disabled={generatingGlobal || !globalBranchId}
              className="ios-button flex items-center justify-center gap-2 px-6 disabled:opacity-50"
            >
              {generatingGlobal ? <Loader2 size={18} className="animate-spin" /> : <CalendarDays size={18} />}
              {generatingGlobal ? 'Timbrando...' : 'Generar y timbrar'}
            </button>
          </div>
        </div>
      )}
 
      <div className="ios-card p-0 overflow-hidden flex flex-col min-h-[500px]">
        {/* Toolbar */}
        <div className="p-4 border-b border-ios-gray-5 flex items-center gap-4 bg-ios-gray-6/30">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, RFC, folio o UUID..."
              className="ios-input w-full pl-10"
            />
          </div>
        </div>
 
        {/* Table */}
        <div className="flex-1 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-ios-blue" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-ios-gray">
              <FileText size={48} className="mb-4 opacity-20" />
              <p className="text-lg font-medium">No se encontraron facturas</p>
              <p className="text-sm">Genera tu primera factura o cambia los filtros de búsqueda.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-6 py-4 font-medium text-ios-gray text-xs uppercase tracking-wider">Detalle</th>
                  <th className="text-left px-6 py-4 font-medium text-ios-gray text-xs uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-6 py-4 font-medium text-ios-gray text-xs uppercase tracking-wider">Monto</th>
                  <th className="text-left px-6 py-4 font-medium text-ios-gray text-xs uppercase tracking-wider">Estado</th>
                  <th className="text-right px-6 py-4 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-ios-dark flex items-center gap-2">
                        {inv.folio}
                        {inv.kind === 'GLOBAL' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-ios-purple/10 text-ios-purple">
                            <Layers size={10} /> GLOBAL {inv.salesCount ? `· ${inv.salesCount} op.` : ''}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-ios-gray mt-0.5">{new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(inv.createdAt))}</p>
                      <p className="text-[10px] text-ios-gray font-mono mt-1" title="UUID (Folio Fiscal)">
                        {inv.uuid || 'Pendiente'}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium truncate max-w-[200px]" title={inv.customerName}>{inv.customerName}</p>
                      <p className="text-xs text-ios-gray mt-0.5">{inv.customerRfc}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold">${Number(inv.grandTotal).toFixed(2)}</p>
                      <p className="text-xs text-ios-gray mt-0.5">MXN</p>
                    </td>
                    <td className="px-6 py-4">
                      {inv.status === 'STAMPED' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-ios-green/10 text-ios-green">
                          <CheckCircle2 size={14} /> Timbrada
                        </span>
                      ) : inv.status === 'CANCELLED' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-ios-red/10 text-ios-red">
                          <XCircle size={14} /> Cancelada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-600">
                          <Loader2 size={14} className="animate-spin" /> Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => handleDownload(inv, 'pdf')}
                          className="p-2 rounded-lg text-ios-gray hover:text-ios-blue hover:bg-ios-blue/10 transition-colors" 
                          title="Descargar PDF"
                        >
                          <Download size={16} />
                        </button>
                        <button 
                          onClick={() => handleDownload(inv, 'xml')}
                          className="p-2 rounded-lg text-ios-gray hover:text-ios-blue hover:bg-ios-blue/10 transition-colors font-mono text-xs font-bold" 
                          title="Descargar XML"
                        >
                          XML
                        </button>
                        {inv.status === 'STAMPED' && (
                          <button 
                            onClick={() => handleCancel(inv)}
                            className="p-2 rounded-lg text-ios-gray hover:text-ios-red hover:bg-ios-red/10 transition-colors ml-2" title="Cancelar Factura"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
